import * as React from "react";
import {
	Alert,
	AlertTitle,
	Box,
	Button,
	Divider,
	Skeleton,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Typography,
} from "@mui/material";
import { BroomIcon } from "@phosphor-icons/react/dist/ssr/Broom";

import type { CampaignRequestDetail, LeadAnalysis } from "@/hooks/api/use-campaign-requests";
import { useCampaignRequestLeadAnalysis, useDownloadExcludedLeads } from "@/hooks/api/use-campaign-requests";

import { DocumentList } from "../../components";
import { formatQuantity, marketFlag, marketLabel } from "../../constants";
import { EmptyPanel, MissingLine } from "./parts";

/**
 * Leads: how many arrived, and how many of them we can dial.
 *
 * Those two numbers are the entire point of this tab and they used to be the last line of a
 * paragraph underneath a five-row table of zeroes, underneath three paragraphs about which parts
 * of the cleaning pipeline had not been written. They lead now. Everything else is subordinate to
 * them and most of it does not render at all until there is something in it.
 */
export function LeadsTab({ request }: { request: CampaignRequestDetail }): React.JSX.Element {
	const hasLeadList = request.documents.some((document) => document.kind === "lead_list");

	return (
		<Stack spacing={3}>
			{hasLeadList ? (
				<LeadCounts request={request} />
			) : (
				<EmptyPanel icon={<BroomIcon size={28} />} title="No lead list has arrived yet">
					The agents still get built and billed while this waits on the client.
				</EmptyPanel>
			)}

			<Divider />

			<DocumentList
				requestId={request.id}
				documents={request.documents}
				filterKinds={["lead_list", "dnc_list"]}
				kinds={["lead_list", "dnc_list"]}
				title="Uploaded lists"
				// Short on purpose: when there is no list the panel above has already said so, and
				// saying it twice on one tab is the thing this screen is being cleared of.
				emptyText="Nothing uploaded yet."
				allowDelete={false}
			/>
		</Stack>
	);
}

function LeadCounts({ request }: { request: CampaignRequestDetail }): React.JSX.Element {
	const { data: analysis, isPending, isError } = useCampaignRequestLeadAnalysis(request.id);
	const downloadMutation = useDownloadExcludedLeads();

	if (isPending) {
		return (
			<Stack spacing={1}>
				<Skeleton variant="rounded" height={64} width={280} />
				<Skeleton variant="rounded" height={120} />
			</Stack>
		);
	}

	if (isError || !analysis) {
		return (
			<Alert severity="warning">
				<AlertTitle>The lists could not be read</AlertTitle>
				<Typography variant="body2">
					The files are stored and intact — we could not read the counts out of them. Open one below to check it by
					hand.
				</Typography>
			</Alert>
		);
	}

	// The removed-rows table earns its place only when something was actually removed, or when the
	// file split across markets. A table of five zeroes is not a report, it is furniture.
	const removed =
		analysis.duplicatesRemoved + analysis.crossMarketDuplicates + analysis.rejected + analysis.excludedNoRecipient;
	const hasBreakdown = analysis.markets.length > 0 || removed > 0;

	return (
		<Stack spacing={2.5}>
			<Stack direction="row" spacing={1} sx={{ alignItems: "flex-start" }}>
				<Stack direction="row" spacing={4} sx={{ flex: "1 1 auto", flexWrap: "wrap" }}>
					<Count value={analysis.rowsIn} label="Rows in" />
					<Count value={analysis.usable} label="To dial" />
				</Stack>

				{/* Only offered when there is a file behind it. UX §07: handing the dropped rows back
				    with a reason on each is the trust-keeping move — offering to download nothing is
				    not. */}
				{analysis.rejected > 0 ? (
					<Button
						size="small"
						disabled={downloadMutation.isPending}
						onClick={() => downloadMutation.mutate(request.id)}
					>
						Download Excluded Rows
					</Button>
				) : null}
			</Stack>

			<Typography variant="body2" color="text.secondary">
				{analysis.phoneColumn
					? `Numbers read from “${analysis.phoneColumn}”.`
					: "We could not tell which column holds the phone number."}
			</Typography>

			{hasBreakdown ? <Breakdown analysis={analysis} /> : null}

			<RecipientExclusion request={request} analysis={analysis} />

			<MissingLine>
				This upload preview helps identify file issues. Processing & Approval shows the cleaned contact groups, samples
				and excluded-row report.
			</MissingLine>
		</Stack>
	);
}

/** One of the two numbers this tab exists to show. Size carries the hierarchy, not colour. */
function Count({ value, label }: { value: number; label: string }): React.JSX.Element {
	return (
		<Box sx={{ minWidth: 96 }}>
			<Typography variant="h4" sx={{ lineHeight: 1.1 }}>
				{formatQuantity(value)}
			</Typography>
			<Typography variant="caption" color="text.secondary">
				{label}
			</Typography>
		</Box>
	);
}

/**
 * Where the rows went and what came out of them.
 *
 * Deliberately the same numbers the client was shown at step 4 — a client told 3,140 contacts and
 * a preparation screen saying something else is the argument this whole feature exists to end.
 * Rows that are zero are dropped, so what is left is only what happened.
 */
function Breakdown({ analysis }: { analysis: LeadAnalysis }): React.JSX.Element {
	const removals: [label: string, rows: number, note: string][] = [
		["Duplicates removed", analysis.duplicatesRemoved, "same number, more than one row"],
		// The billing guard: without it each of these gets double the agreed attempts and we bill
		// for them.
		["In two markets — deduped", analysis.crossMarketDuplicates, "kept in the first market they appeared in"],
		[
			"Left out — nobody to notify",
			analysis.excludedNoRecipient,
			`empty “${analysis.recipientColumn ?? "recipient"}”, at the client's request`,
		],
		["Rejected — unusable number", analysis.rejected, "not a number we can dial"],
	];

	const shownRemovals = removals.filter(([, rows]) => rows > 0);

	return (
		<TableContainer sx={{ overflowX: "auto" }}>
			<Table size="small">
				<TableHead>
					<TableRow>
						<TableCell sx={{ fontWeight: 600 }}>What We Found</TableCell>
						<TableCell sx={{ fontWeight: 600 }} align="right">
							Rows
						</TableCell>
						<TableCell sx={{ fontWeight: 600 }}>Goes To</TableCell>
					</TableRow>
				</TableHead>
				<TableBody>
					{analysis.markets.map((split) => (
						<TableRow key={split.market}>
							<TableCell>
								{marketFlag(split.market)} {marketLabel(split.market)}
								{split.callingCode ? ` · ${split.callingCode}` : ""}
							</TableCell>
							<TableCell align="right">{formatQuantity(split.rows)}</TableCell>
							<TableCell>
								{split.routed ? (
									(split.agentLabel ?? "No agent on this market yet")
								) : (
									<Typography variant="body2" color="warning.main">
										Not a market on this request — these are not called
									</Typography>
								)}
							</TableCell>
						</TableRow>
					))}

					{shownRemovals.map(([label, rows, note]) => (
						<TableRow key={label}>
							<TableCell>{label}</TableCell>
							<TableCell align="right">{formatQuantity(rows)}</TableCell>
							<TableCell>{note}</TableCell>
						</TableRow>
					))}
				</TableBody>
			</Table>
		</TableContainer>
	);
}

/**
 * The client's own instruction, and whether we were able to carry it out.
 *
 * A request to leave contacts out that we cannot honour is the one state on this tab worth
 * interrupting for: the counts above look entirely normal and every one of those contacts would be
 * dialled. Honoured, it is a line; unhonourable, it is an alert.
 */
function RecipientExclusion({
	request,
	analysis,
}: {
	request: CampaignRequestDetail;
	analysis: LeadAnalysis;
}): React.JSX.Element | null {
	if (!request.notification?.excludeContactsWithoutRecipient) return null;

	if (analysis.recipientColumn) {
		return (
			<Typography variant="body2" color="text.secondary">
				{`${formatQuantity(analysis.excludedNoRecipient)} contact${
					analysis.excludedNoRecipient === 1 ? "" : "s"
				} with no “${analysis.recipientColumn}” are already out of the counts above, at the client's request.`}
			</Typography>
		);
	}

	return (
		<Alert severity="warning">
			<AlertTitle>The client asked us to leave out contacts with no notification recipient — we cannot</AlertTitle>
			<Typography variant="body2">
				{`Their notification is addressed to ${
					request.notification.toTemplate ?? "nobody"
				}, and the uploaded list has no column behind it — so honouring the request literally would drop the whole list. Nothing has been excluded. Get the column named, or the address changed, before loading.`}
			</Typography>
		</Alert>
	);
}
